import type { CodeableConcept, PractitionerRole } from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from './namespace';

/**
 * The practice-role vocabulary stamped on `PractitionerRole.code`: is this
 * member a clinician (sees patients, owns encounters) or front-desk staff
 * (books appointments, no clinical documentation)?
 *
 * Deliberately eluve's own code system rather than a SNOMED/v2 role set — the
 * distinction we need is an access-shape fact about our product, not a
 * clinical taxonomy. The system string matches the scheduling POC so data
 * written there stays readable here.
 *
 * Deliberately NOT the eluve `tenant_users.role` (tenant-admin/tenant-member):
 * that is an eluve authz concept; this is a FHIR-side care-team fact. A
 * tenant-admin can be a clinician; a tenant-member can be front desk.
 */
export const FHIR_PRACTITIONER_ROLE_CODE_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/fhir/practitioner-role`;

export const PRACTITIONER_ROLE_CODES = ['clinician', 'front-desk'] as const;

export type PractitionerRoleCode = (typeof PRACTITIONER_ROLE_CODES)[number];

const ROLE_DISPLAYS: Record<PractitionerRoleCode, string> = {
  clinician: 'Clinician',
  'front-desk': 'Front desk',
};

function isPractitionerRoleCode(code: string): code is PractitionerRoleCode {
  return (PRACTITIONER_ROLE_CODES as readonly string[]).includes(code);
}

/** Display label for a practice-role code; unknown codes render as-is. */
export function practiceRoleLabel(code: string): string {
  return isPractitionerRoleCode(code) ? ROLE_DISPLAYS[code] : code;
}

export function practitionerRoleConcept(
  code: PractitionerRoleCode,
): CodeableConcept {
  return {
    coding: [
      {
        system: FHIR_PRACTITIONER_ROLE_CODE_SYSTEM,
        code,
        display: ROLE_DISPLAYS[code],
      },
    ],
    text: ROLE_DISPLAYS[code],
  };
}

/**
 * The practice role carried by a practitioner's roles, or undefined when none
 * of them stamps our system (e.g. roles synced from an external booking
 * system). Front-desk wins over clinician across multiple roles — the
 * booking-only shape is the restrictive one, so a stray clinician role must
 * not silently upgrade a front-desk member.
 */
export function practitionerRoleCodeOf(
  roles: readonly PractitionerRole[],
): PractitionerRoleCode | undefined {
  const codes = new Set(
    roles
      .flatMap((role) => role.code ?? [])
      .flatMap((concept) => concept.coding ?? [])
      .filter((coding) => coding.system === FHIR_PRACTITIONER_ROLE_CODE_SYSTEM)
      .map((coding) => coding.code),
  );
  if (codes.has('front-desk')) return 'front-desk';
  if (codes.has('clinician')) return 'clinician';
  return undefined;
}
